import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Space, message } from 'antd';
import { PlusOutlined, EditOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { apiClient } from 'Utils/api';
import { useIsMobile } from 'Utils/useIsMobile';
import MobileListLayout from '../Components/MobileList/MobileListLayout';
import MobileCard from '../Components/MobileList/MobileCard';

export interface GiveawayListItem {
  id: string;
  title: string;
  slug: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  status: 'deactivated' | 'planned' | 'running' | 'ended';
  entryCount: number;
  confirmedCount: number;
}

export const GIVEAWAY_STATUS: Record<string, { label: string; color: string }> = {
  planned: { label: 'Geplant', color: 'blue' },
  running: { label: 'Läuft', color: 'green' },
  ended: { label: 'Beendet', color: 'default' },
  deactivated: { label: 'Deaktiviert', color: 'red' },
};

export const formatRange = (startsAt: string, endsAt: string) =>
  `${dayjs(startsAt).format('DD.MM.YYYY HH:mm')} – ${dayjs(endsAt).format('DD.MM.YYYY HH:mm')}`;

const GiveawaysPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [giveaways, setGiveaways] = useState<GiveawayListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get('/api/admin/giveaways');
        setGiveaways(res.data.giveaways || []);
      } catch (e: any) {
        message.error(e.response?.data?.message || 'Fehler beim Laden der Gewinnspiele');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const statusTag = (g: GiveawayListItem) => <Tag color={GIVEAWAY_STATUS[g.status]?.color}>{GIVEAWAY_STATUS[g.status]?.label}</Tag>;
  const newButton = <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/giveaways/new')}>Neues Gewinnspiel</Button>;

  if (isMobile) {
    const term = search.toLowerCase();
    const visible = giveaways.filter((g) => !term || g.title.toLowerCase().includes(term) || g.slug.includes(term));
    return (
      <MobileListLayout
        title="Gewinnspiele"
        action={newButton}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Titel oder Slug suchen …"
        count={`${visible.length} ${visible.length === 1 ? 'Gewinnspiel' : 'Gewinnspiele'}`}
        loading={loading}
        isEmpty={visible.length === 0}
        emptyTitle="Keine Gewinnspiele gefunden"
        emptyText={search ? 'Kein Gewinnspiel passt zur Suche.' : 'Lege über „Neues Gewinnspiel“ das erste Gewinnspiel an.'}
        onReset={search ? () => setSearch('') : undefined}
      >
        {visible.map((g) => (
          <MobileCard
            key={g.id}
            testId={`mobile-giveaway-card-${g.id}`}
            overline={g.slug}
            title={g.title}
            highlight={<>{statusTag(g)}<span style={{ fontWeight: 400, fontSize: 13 }}>{g.confirmedCount} / {g.entryCount} Teilnahmen</span></>}
            meta={formatRange(g.startsAt, g.endsAt)}
            onOpen={() => navigate(`/giveaways/${g.id}`)}
            actions={[
              { key: 'edit', label: 'Bearbeiten', icon: <EditOutlined />, onClick: () => navigate(`/giveaways/${g.id}`) },
              { key: 'entries', label: 'Teilnehmer', icon: <TeamOutlined />, onClick: () => navigate(`/giveaways/${g.id}?tab=entries`) },
            ]}
          />
        ))}
      </MobileListLayout>
    );
  }

  const columns = [
    { title: 'Titel', dataIndex: 'title', key: 'title' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: 'Zeitraum', key: 'range', render: (_: any, g: GiveawayListItem) => formatRange(g.startsAt, g.endsAt) },
    { title: 'Status', key: 'status', render: (_: any, g: GiveawayListItem) => statusTag(g) },
    {
      title: 'Teilnahmen',
      key: 'entries',
      render: (_: any, g: GiveawayListItem) => `${g.confirmedCount} bestätigt / ${g.entryCount} gesamt`,
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_: any, g: GiveawayListItem) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => navigate(`/giveaways/${g.id}`)}>Bearbeiten</Button>
          <Button type="link" icon={<TeamOutlined />} onClick={() => navigate(`/giveaways/${g.id}?tab=entries`)}>Teilnehmer</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h1>Gewinnspiele</h1>
        {newButton}
      </div>
      <Table rowKey="id" columns={columns} dataSource={giveaways} loading={loading} pagination={{ pageSize: 20 }} />
    </div>
  );
};

export default GiveawaysPage;
